import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { draftHook, createHook, updateHook } from '../../api/hooks';
import {
    hookToForm, formToPayload, validateForm,
    QUESTION_MAX, OPTION_MAX, EXPLANATION_MAX, BRIDGE_MAX,
} from './hookForm';
import HookPreview from './HookPreview';
import './hooks.css';

// Create or edit one hook (CONTEXT.md "Hook") for a course. "Generate"
// asks the backend drafter for a proposal and fills the form; nothing is
// saved until Save. Saving changed content returns an approved hook to
// draft (the server does that) — the modal says so.
export default function HookEditModal({ visible, hook, course, token, onClose }) {
    const [form, setForm] = useState(hookToForm(hook));
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [serverErrors, setServerErrors] = useState([]);

    useEffect(() => {
        if (visible) {
            setForm(hookToForm(hook));
            setTouched(false);
            setSaving(false);
            setGenerating(false);
            setServerErrors([]);
        }
    }, [visible, hook]);

    const errors = validateForm(form);
    const hasErrors = Object.keys(errors).length > 0;
    const busy = saving || generating;
    const isNew = !hook?.id;

    const setField = (field) => (e) => {
        const value = e.target.type === 'radio' ? Number(e.target.value) : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleCancel = useCallback(() => {
        onClose({ saved: false, hook: null });
    }, [onClose]);

    const generate = useCallback(async () => {
        if (!course?.id) return;
        setServerErrors([]);
        setGenerating(true);
        try {
            setForm(hookToForm(await draftHook(token, course.id)));
            setTouched(true);
        } catch (err) {
            setServerErrors([err.message || 'Generation failed, please try again.']);
        } finally {
            setGenerating(false);
        }
    }, [course?.id, token]);

    const save = useCallback(async () => {
        setTouched(true);
        if (hasErrors || !course?.id) return;
        setServerErrors([]);
        setSaving(true);
        try {
            const payload = formToPayload(form);
            const saved = isNew
                ? await createHook(token, course.id, payload)
                : await updateHook(token, hook.id, payload);
            onClose({ saved: true, hook: saved });
        } catch (err) {
            setServerErrors(err.errors?.length ? err.errors : [err.message || 'Saving failed, please try again.']);
            setSaving(false);
        }
    }, [hasErrors, course?.id, isNew, hook?.id, token, form, onClose]);

    const showError = (field) => touched && errors[field];
    const counter = (value, max) => (
        <span className={`hk-count${(value || '').trim().length > max ? ' over' : ''}`}>
            {(value || '').trim().length}/{max}
        </span>
    );

    const header = isNew
        ? `New hook${course?.name ? `: ${course.name}` : ''}`
        : `Edit hook${course?.name ? `: ${course.name}` : ''}`;

    return (
        <Dialog
            visible={visible}
            header={header}
            modal
            style={{ width: '560px', maxWidth: '95vw' }}
            onHide={handleCancel}
            className="hook-dialog"
        >
            <div className="hk-body">
                <p className="hk-desc">
                    A guessable question about one surprising fact from the course. The app shows
                    the question with the two options, then the verdict and your explanation.
                    {hook?.status === 'approved' && (
                        <> <strong>This hook is approved: changing its content returns it to draft.</strong></>
                    )}
                </p>

                {serverErrors.length > 0 && (
                    <div className="hk-server-errors">
                        Not saved:
                        <ul>{serverErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </div>
                )}

                <div className="hk-field">
                    <div className="hk-label">
                        <span>Question</span>
                        {counter(form.question, QUESTION_MAX)}
                    </div>
                    <input
                        className={`hk-input${showError('question') ? ' invalid' : ''}`}
                        value={form.question}
                        disabled={busy}
                        onChange={setField('question')}
                        placeholder="Is a banana a berry?"
                    />
                    {showError('question') && <div className="hk-error">{errors.question}</div>}
                </div>

                <div className="hk-field">
                    <div className="hk-label"><span>Options (tick the correct one)</span></div>
                    <div className="hk-options">
                        {[['optionA', 0], ['optionB', 1]].map(([field, index]) => (
                            <div key={field}>
                                <label className="hk-option">
                                    <input
                                        type="radio"
                                        name="correctIndex"
                                        value={index}
                                        checked={form.correctIndex === index}
                                        disabled={busy}
                                        onChange={setField('correctIndex')}
                                    />
                                    <input
                                        className={`hk-input${showError(field) ? ' invalid' : ''}`}
                                        value={form[field]}
                                        disabled={busy}
                                        onChange={setField(field)}
                                        maxLength={OPTION_MAX + 4}
                                    />
                                </label>
                                {showError(field) && <div className="hk-error">{errors[field]}</div>}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="hk-field">
                    <div className="hk-label">
                        <span>Explanation (one sentence, follows “The answer is {form[form.correctIndex === 1 ? 'optionB' : 'optionA'] || '…'}.”)</span>
                        {counter(form.explanation, EXPLANATION_MAX)}
                    </div>
                    <textarea
                        className={`hk-textarea${showError('explanation') ? ' invalid' : ''}`}
                        rows={2}
                        value={form.explanation}
                        disabled={busy}
                        onChange={setField('explanation')}
                        placeholder="Botanically a banana is a berry, and a strawberry is not."
                    />
                    {showError('explanation') && <div className="hk-error">{errors.explanation}</div>}
                </div>

                <div className="hk-field">
                    <div className="hk-label">
                        <span>Bridge (optional; the app has a default line)</span>
                        {counter(form.bridge, BRIDGE_MAX)}
                    </div>
                    <input
                        className={`hk-input${showError('bridge') ? ' invalid' : ''}`}
                        value={form.bridge}
                        disabled={busy}
                        onChange={setField('bridge')}
                        placeholder="Leave empty to use the app's default"
                    />
                    {showError('bridge') && <div className="hk-error">{errors.bridge}</div>}
                </div>

                <HookPreview form={form} courseName={course?.name} title="In the app, after a guess" />

                <div className="hk-actions">
                    <button type="button" className="hk-btn hk-btn-generate" disabled={busy} onClick={generate}>
                        <i className={`pi ${generating ? 'pi-spin pi-spinner' : 'pi-star'}`} />
                        {generating ? 'Generating…' : 'Generate with AI'}
                    </button>
                    <div className="hk-actions-right">
                        <button type="button" className="hk-btn" disabled={busy} onClick={handleCancel}>Cancel</button>
                        <button type="button" className="hk-btn hk-btn-primary" disabled={busy || (touched && hasErrors)} onClick={save}>
                            {saving ? 'Saving…' : isNew ? 'Create draft' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

HookEditModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    hook: PropTypes.shape({
        id: PropTypes.number,
        question: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        correct_index: PropTypes.number,
        explanation: PropTypes.string,
        bridge: PropTypes.string,
        status: PropTypes.string,
    }),
    course: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    token: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

HookEditModal.defaultProps = { hook: null, course: null };
